// PictureFrameTool.jsx
// ============================================================================
// NON-DESTRUCTIVE FRAME EDITOR (Schema v4)
// ----------------------------------------------------------------------------
// This component is a pure frame-metadata editor. It does NOT generate or
// persist composite snapshots. Its responsibilities are:
//
//   1. Manage the user's uploaded artwork gallery (thumbnails, upload state).
//   2. Let the user place / edit / remove frames on the canvas.
//   3. Report frame metadata (corners, rotation, borders, crop info) to the
//      parent Editor via `onFrameApplied` / `onFramesUpdate`.
//   4. Render the editing overlay (marching ants, corner handles, control
//      cluster, rotation gizmo, frame-style panel, crop overlay).
//
// The parent Editor is solely responsible for compositing: it walks the
// working base (canvasImage) + every placed frame at render time and produces
// the visible canvas. This component never produces a composite.
//
// The overlay's background during editing is the CURRENT working base
// (`canvasImage` prop), not a stored per-frame snapshot. This is what makes
// frame edits non-destructive and what fixes the "edit reverts to original
// position" regression.
//
// ALL UI IS PRESERVED EXACTLY. Only the data flow beneath the UI has changed.
// ============================================================================

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { FiUpload, FiPlus, FiEdit2, FiX, FiImage, FiCheck, FiRefreshCw, FiLock, FiUnlock } from 'react-icons/fi';
import { RiPaintingLine } from 'react-icons/ri';
import { LuRotate3D, LuReplace } from 'react-icons/lu';
import { IoIosMove } from 'react-icons/io';
import { IoCropSharp } from 'react-icons/io5';
import { MdBorderStyle } from 'react-icons/md';
import { getStorage, ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase';

// ============================================================================
// HEIC / HEIF CONVERSION
// ----------------------------------------------------------------------------
// Identical rationale to LayersComponent.jsx. iPhones shoot HEIC by default;
// no browser except Safari 17+ can decode it. Convert to JPEG before the
// FileReader turns it into an unreadable data URL.
// ============================================================================
const convertHeicToJpeg = async (file) => {
  const isDng = /\.dng$/i.test(file.name) || file.type === 'image/dng' || file.type === 'image/x-adobe-dng';
  if (isDng) {
    throw new Error('DNG (camera raw) files are not supported yet. Please export your photo as JPEG or HEIC before uploading.');
  }
  const isHeic = /\.(heic|heif)$/i.test(file.name) ||
    file.type === 'image/heic' ||
    file.type === 'image/heif';
  if (!isHeic) return file;

  try {
    const heic2any = (await import('heic2any')).default;
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
    const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch (err) {
    console.error('[HEIC] conversion failed:', err);
    throw new Error('Could not read this HEIC photo. Try exporting it as JPEG first.');
  }
};

// Helper to convert any image source to a data URL (if needed)
const loadImageAsDataUrl = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error('No source provided'));
    if (src.startsWith('data:')) {
      resolve(src);
      return;
    }
    // Attempt to fetch and convert to data URL
    fetch(src, { mode: 'cors' })
      .then(response => {
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
};

// ============================================================================
// FRAME ARTWORK — MEMOIZED IMG
// ----------------------------------------------------------------------------
// CRITICAL: This component MUST live at module scope. Declaring it inside
// the PictureFrameTool body creates a NEW function identity on every render.
// React compares component types by reference, so each render was seen as a
// DIFFERENT component type, causing React to unmount + remount the entire
// <img> subtree on every parent render.
//
// During a frame drag, PictureFrameTool re-renders on every animation frame
// (~60/sec). With the inline definition, React was remounting every frame's
// <img> 60x/sec, forcing the browser to re-decode the full-resolution
// artwork on every tick. Desktop survived this (more heap, faster decode);
// mobile OOM-killed the tab within a second or two of the first touch.
//
// Hoisting the definition here gives React a STABLE component identity.
// Drag-driven re-renders are now just prop diffs: the <img> DOM node is
// preserved across ticks and the browser reuses the decoded bitmap.
//
// React.memo is kept as a further optimisation — it prevents the child from
// re-rendering when its props are referentially equal. Even when the parent
// passes new callback references on each render, the child re-render is
// cheap (React diffs props, src is unchanged, the DOM node is untouched).
// ============================================================================
const FrameArtwork = React.memo(function FrameArtwork({ src, alt, onMouseDown, onTouchStart, cursor }) {
  return (
    <img
      src={src}
      alt={alt}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      draggable={false}
      decoding="async"
      loading="eager"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        maxWidth: 'none',
        maxHeight: 'none',
        userSelect: 'none',
        cursor,
      }}
    />
  );
});

const PictureFrameTool = forwardRef(({ isOpen, onClose, canvasImage, originalBackgroundImage, onFrameApplied, onFrameRemoved, authUser, showToast, desktopCanvasRef, mobileCanvasRef, savedFrames = [], onFramesUpdate }, ref) => {
  // Completely override showToast to prevent any toast notifications from this component
  showToast = () => {};

  // All hooks must be called BEFORE any conditional returns
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [showReview, setShowReview] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showCanvasOverlay, setShowCanvasOverlay] = useState(false);
  const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [bgNaturalWidth, setBgNaturalWidth] = useState(null);
  const [bgNaturalHeight, setBgNaturalHeight] = useState(null);
  const fileInputRef = useRef(null);
  const prevIsOpenRef = useRef(isOpen);
  const hasInitializedFramesRef = useRef(false);
  const originalCornersBeforeCropRef = useRef(null);
  const [activePulseCorner, setActivePulseCorner] = useState(0);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [interactionMode, setInteractionMode] = useState('move'); // 'move' or 'rotate'
  const isRotatingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  // New states for frame style
  const [showFrameStyleOverlay, setShowFrameStyleOverlay] = useState(false);
  const [frameStyle, setFrameStyle] = useState({
    borderWidth: 0,
    borderType: 'solid',
    borderColor: '#000000',
    gradientStartColor: '#000000',
    gradientEndColor: '#ffffff',
  });

  // Crop overlay states (replacing crop modal)
  const [showCropOverlay, setShowCropOverlay] = useState(false);
  const [cropRect, setCropRect] = useState(null); // { x, y, width, height } in container coords
  const [cropInitialBounds, setCropInitialBounds] = useState(null); // initial bounding box for clamping
  const [cropDrag, setCropDrag] = useState(null); // { type, startX, startY, initialRect, handle? }
  const [cropAspectLocked, setCropAspectLocked] = useState(true); // lock aspect ratio during resize

  // isMobile detection (stateful)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 767
  );

  // Listen for resize events to update isMobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load background image dimensions
  useEffect(() => {
    if (canvasImage) {
      const img = new Image();
      img.onload = () => {
        setBgNaturalWidth(img.naturalWidth);
        setBgNaturalHeight(img.naturalHeight);
      };
      img.src = canvasImage;
    }
  }, [canvasImage]);

  // Warm the browser's decoded-image cache for every artwork URL we know
  // about. Without this, the drag loop re-decodes the full-resolution source
  // on every mousemove, which is what makes the first drag of a freshly
  // uploaded image feel laggy for several seconds until the browser caches
  // the decoded bitmap.
  const warmImageCache = (urls) => {
    if (!urls || urls.length === 0) return;
    urls.forEach(src => {
      if (!src) return;
      const pre = new Image();
      pre.decoding = 'async';
      pre.crossOrigin = 'anonymous';
      pre.src = src;
    });
  };

  // Populate uploadedImages from savedFrames when tool opens.
  // Re-runs if bgNaturalWidth/Height arrive after the first pass so corners
  // get rescaled once dimensions are known.
  //
  // NON-DESTRUCTIVE MODEL NOTES:
  //   - `backgroundImage` is intentionally NOT set on frames. The overlay
  //     uses the live `canvasImage` prop for its editing background instead.
  //   - `rotation` is preserved from persisted metadata so the composite
  //     rebuild downstream sees the correct transform.
  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      return;
    }
    // First open (false→true) OR dimensions just became available.
    const justOpened = !prevIsOpenRef.current;
    const dimensionsJustArrived = bgNaturalWidth && bgNaturalHeight && savedFrames.length > 0 && uploadedImages.length === 0;

    // Fresh project with no saved frames — mark the session as initialized so
    // the frames-sync effect is permitted to persist new uploads. Without this,
    // unplaced uploads on a brand-new project are dropped on close.
    if (justOpened && savedFrames.length === 0) {
      hasInitializedFramesRef.current = true;
      prevIsOpenRef.current = isOpen;
      return;
    }

    if (!(justOpened || dimensionsJustArrived) || savedFrames.length === 0) {
      prevIsOpenRef.current = isOpen;
      return;
    }

    const newImages = savedFrames.map((frame) => {
      let corners = frame.corners;
      let validCorners = false;
      if (corners && Array.isArray(corners) && corners.length === 4) {
        validCorners = corners.every(c => typeof c.x === 'number' && typeof c.y === 'number' && isFinite(c.x) && isFinite(c.y));
      }
      if (!validCorners) {
        corners = null;
      } else if (bgNaturalWidth && bgNaturalHeight) {
        const maxVal = Math.max(...corners.flatMap(c => [c.x, c.y]));
        if (maxVal <= 1.0 && maxVal >= 0) {
          corners = corners.map(c => ({
            x: c.x * bgNaturalWidth,
            y: c.y * bgNaturalHeight
          }));
        }
      }
      const originalData = frame.originalImage || frame.imageData;
      const dataUrl = originalData?.startsWith('data:') ? originalData : null;
      const originalSourceDataUrl = frame.originalUncroppedData || dataUrl || originalData;
      return {
        id: frame.id || Date.now() + Math.random(),
        data: frame.originalImage || frame.imageData,
        originalImageData: originalData,
        dataUrl: dataUrl,
        name: frame.name || 'Frame',
        naturalWidth: frame.naturalWidth || 600,
        naturalHeight: frame.naturalHeight || 400,
        corners: corners || null,
        // Preserve whatever placed/unplaced state was persisted.
        isPlaced: frame.isPlaced !== undefined ? frame.isPlaced : !!corners,
        scale: frame.scale || 1,
        // backgroundImage is intentionally not populated — the overlay uses
        // the live canvasImage prop for its editing backdrop.
        zIndex: frame.zIndex || 0,
        rotation: frame.rotation || { x: 0, y: 0 },
        borderWidth: frame.borderWidth || 0,
        borderType: frame.borderType || 'solid',
        borderColor: frame.borderColor || '#000000',
        gradientStartColor: frame.gradientStartColor || '#000000',
        gradientEndColor: frame.gradientEndColor || '#ffffff',
        originalSourceDataUrl: originalSourceDataUrl,
        originalSourceNaturalWidth: frame.originalSourceNaturalWidth || frame.naturalWidth || 600,
        originalSourceNaturalHeight: frame.originalSourceNaturalHeight || frame.naturalHeight || 400,
        originalCorners: corners ? corners.map(c => ({ ...c })) : (frame.originalCorners || null),
        cropApplied: frame.cropApplied || false,
        isUploading: false,
        uploadProgress: 100,
      };
    });
    newImages.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    setUploadedImages(newImages);
    // Warm the decoded-image cache for every artwork we just loaded so
    // subsequent drag operations are smooth.
    warmImageCache(newImages.map(img => img.originalImageData || img.dataUrl || img.data));
    if (newImages.length > 0 && selectedImageIndex === null) {
      setSelectedImageIndex(0);
    }
    hasInitializedFramesRef.current = true;
    prevIsOpenRef.current = isOpen;
  }, [isOpen, savedFrames, bgNaturalWidth, bgNaturalHeight, uploadedImages.length, selectedImageIndex]);

  // Auto-open mobile panel when tool opens (reveal Gallery tab)
  useEffect(() => {
    if (isOpen && isMobile) {
      setMobilePanelVisible(true);
    }
  }, [isOpen, isMobile]);

  // Reset editing state when tool closes
  useEffect(() => {
    if (!isOpen) {
      // If we were editing and the tool is being closed, auto‑save the current edit
      if (isEditing && showCanvasOverlay && selectedImageIndex !== null && uploadedImages[selectedImageIndex]?.isPlaced) {
        handleSaveChanges();
      }
      setIsEditing(false);
      setShowCanvasOverlay(false);
      setMobilePanelVisible(false);
      hasInitializedFramesRef.current = false;
      setShowFrameStyleOverlay(false);
      setShowCropOverlay(false);
      setCropRect(null);
      setCropInitialBounds(null);
      setCropDrag(null);
    }
  }, [isOpen, isEditing, showCanvasOverlay, selectedImageIndex, uploadedImages]);

  // Pulse corners sequentially
  useEffect(() => {
    if (!showCanvasOverlay || !isEditing || showCropOverlay) return;
    const interval = setInterval(() => {
      setActivePulseCorner(prev => (prev + 1) % 4);
    }, 1200);
    return () => clearInterval(interval);
  }, [showCanvasOverlay, isEditing, showCropOverlay]);

  // Ensure container is positioned relative for correct portal placement
  useEffect(() => {
    if (showCanvasOverlay && !showCropOverlay) {
      const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
      if (container) {
        const computed = getComputedStyle(container);
        if (computed.position === 'static') {
          container.style.position = 'relative';
        }
      }
    }
  }, [showCanvasOverlay, isMobile, mobileCanvasRef, desktopCanvasRef, showCropOverlay]);

  // Set touch-action on container when editing to prevent scroll interference
  useEffect(() => {
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (container && showCanvasOverlay && isEditing && !showCropOverlay) {
      container.style.touchAction = 'none';
      return () => {
        container.style.touchAction = 'auto';
      };
    }
  }, [showCanvasOverlay, isEditing, isMobile, mobileCanvasRef, desktopCanvasRef, showCropOverlay]);

  // Rotation effect for mouse drag, wheel, and touch (with angle clamping)
  useEffect(() => {
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container || !showCanvasOverlay || !isEditing || showCropOverlay) return;

    let isDragging = false;
    let lastPos = { x: 0, y: 0 };

    const clampRotation = (rot) => ({
      x: Math.max(-75, Math.min(75, rot.x)),
      y: Math.max(-75, Math.min(75, rot.y)),
    });

    const handleMouseDown = (e) => {
      if (interactionMode !== 'rotate') return;
      if (e.target.closest('[data-control-area="true"]')) return;
      e.preventDefault();
      isDragging = true;
      lastPos = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
      if (!isDragging || interactionMode !== 'rotate') return;
      e.preventDefault();
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      setRotation(prev => clampRotation({
        x: prev.x + dy * 0.5,
        y: prev.y + dx * 0.5
      }));
      lastPos = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    const handleWheel = (e) => {
      if (interactionMode !== 'rotate') return;
      if (e.target.closest('[data-control-area="true"]')) return;
      e.preventDefault();
      setRotation(prev => clampRotation({
        x: prev.x + e.deltaY * 0.2,
        y: prev.y + e.deltaX * 0.2
      }));
    };

    const handleTouchStart = (e) => {
      if (interactionMode !== 'rotate') return;
      if (e.touches.length !== 1) return;
      if (e.target.closest('[data-control-area="true"]')) return;
      isDragging = true;
      lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchMove = (e) => {
      if (!isDragging || interactionMode !== 'rotate') return;
      if (e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - lastPos.x;
      const dy = touch.clientY - lastPos.y;
      setRotation(prev => clampRotation({
        x: prev.x + dy * 0.5,
        y: prev.y + dx * 0.5
      }));
      lastPos = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = () => {
      isDragging = false;
    };

    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [showCanvasOverlay, isEditing, isMobile, mobileCanvasRef, desktopCanvasRef, interactionMode, showCropOverlay]);

  // Global canvas drag for move tool
  useEffect(() => {
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container || !showCanvasOverlay || !isEditing || interactionMode !== 'move' || showCropOverlay) return;

    const handleCanvasDragStart = (e) => {
      if (selectedImageIndex === null) return;
      const img = uploadedImages[selectedImageIndex];
      if (!img || !img.corners) return;
      
      if (e.target.closest('[data-control-area="true"]')) return;
      
      const point = e.touches ? e.touches[0] : e;
      setDragState({
        type: 'image',
        startX: point.clientX,
        startY: point.clientY,
        initialCorners: img.corners.map(c => ({ ...c })),
      });
    };

    container.addEventListener('mousedown', handleCanvasDragStart);
    container.addEventListener('touchstart', handleCanvasDragStart, { passive: true });

    return () => {
      container.removeEventListener('mousedown', handleCanvasDragStart);
      container.removeEventListener('touchstart', handleCanvasDragStart);
    };
  }, [showCanvasOverlay, isEditing, isMobile, mobileCanvasRef, desktopCanvasRef, interactionMode, selectedImageIndex, uploadedImages, showCropOverlay]);

  // Unified drag/resize effect (works on pixel coordinates)
  // Throttled to one state update per animation frame so rapid mousemove
  // events don't trigger redundant React reconciliation and image decodes.
  useEffect(() => {
    if (!dragState) return;

    let rafId = null;
    let pendingEvent = null;

    const applyMove = () => {
      rafId = null;
      if (!pendingEvent) return;
      const e = pendingEvent;
      pendingEvent = null;
      if (!dragState) return;
      const point = e.touches ? e.touches[0] : e;
      const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const deltaX = point.clientX - dragState.startX;
      const deltaY = point.clientY - dragState.startY;

      const { offsetX, offsetY, scale } = getImageRenderParams(rect);
      const imgDeltaX = deltaX / scale;
      const imgDeltaY = deltaY / scale;

      setUploadedImages(prev => prev.map((img, idx) => {
        if (idx !== selectedImageIndex) return img;
        if (dragState.type === 'image') {
          return {
            ...img,
            corners: dragState.initialCorners.map(c => ({
              x: c.x + imgDeltaX,
              y: c.y + imgDeltaY,
            })),
          };
        } else if (dragState.type === 'corner') {
          const newCorners = [...img.corners];
          newCorners[dragState.cornerIndex] = {
            x: dragState.initialCorners[dragState.cornerIndex].x + imgDeltaX,
            y: dragState.initialCorners[dragState.cornerIndex].y + imgDeltaY,
          };
          return { ...img, corners: newCorners };
        }
        return img;
      }));
    };

    const handleMove = (e) => {
      if (!dragState) return;
      e.preventDefault();
      pendingEvent = e;
      if (rafId === null) {
        rafId = requestAnimationFrame(applyMove);
      }
    };

    const handleEnd = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingEvent = null;
      setDragState(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [dragState, selectedImageIndex, isMobile, desktopCanvasRef, mobileCanvasRef]);

  // Helper: compute background render parameters (scale, offset) and image display rect
  const getImageRenderParams = (rect) => {
    if (!bgNaturalWidth || !bgNaturalHeight) {
      return { offsetX: 0, offsetY: 0, scale: 1, imageDisplayRect: null };
    }
    const containerAspect = rect.width / rect.height;
    const imageAspect = bgNaturalWidth / bgNaturalHeight;
    let renderWidth, renderHeight, offsetX, offsetY, scale;

    if (isMobile) {
      // object-fit: cover - image fills container, may crop
      if (containerAspect > imageAspect) {
        renderWidth = rect.width;
        scale = renderWidth / bgNaturalWidth;
        renderHeight = bgNaturalHeight * scale;
        offsetX = 0;
        offsetY = (rect.height - renderHeight) / 2;
      } else {
        renderHeight = rect.height;
        scale = renderHeight / bgNaturalHeight;
        renderWidth = bgNaturalWidth * scale;
        offsetX = (rect.width - renderWidth) / 2;
        offsetY = 0;
      }
    } else {
      // object-fit: contain - image fits inside, letterbox
      if (containerAspect > imageAspect) {
        renderHeight = rect.height;
        scale = renderHeight / bgNaturalHeight;
        renderWidth = bgNaturalWidth * scale;
        offsetX = (rect.width - renderWidth) / 2;
        offsetY = 0;
      } else {
        renderWidth = rect.width;
        scale = renderWidth / bgNaturalWidth;
        renderHeight = bgNaturalHeight * scale;
        offsetX = 0;
        offsetY = (rect.height - renderHeight) / 2;
      }
    }

    const imageDisplayRect = {
      x: offsetX,
      y: offsetY,
      width: renderWidth,
      height: renderHeight,
    };

    return { offsetX, offsetY, scale, imageDisplayRect };
  };

  // Convert container coordinates to image pixel coordinates
  const containerToImage = (containerX, containerY, rect) => {
    const { offsetX, offsetY, scale } = getImageRenderParams(rect);
    return {
      x: (containerX - offsetX) / scale,
      y: (containerY - offsetY) / scale,
    };
  };

  // Convert image pixel coordinates to container coordinates
  const imageToContainer = (imgX, imgY, rect) => {
    const { offsetX, offsetY, scale } = getImageRenderParams(rect);
    return {
      x: imgX * scale + offsetX,
      y: imgY * scale + offsetY,
    };
  };

  // Apply 3D rotation to corners (pixel coordinates) around their center.
  // Kept for the on-canvas overlay's marching-ants transform. The Editor's
  // composite build re-applies the same maths when rendering frames.
  const applyRotationToCorners = (corners, rotX, rotY) => {
    const baseCorners = corners;
    const center = baseCorners.reduce((acc, c) => ({
      x: acc.x + c.x / 4,
      y: acc.y + c.y / 4,
    }), { x: 0, y: 0 });
    
    const radX = (rotX * Math.PI) / 180;
    const radY = (rotY * Math.PI) / 180;
    const cosX = Math.cos(radX), sinX = Math.sin(radX);
    const cosY = Math.cos(radY), sinY = Math.sin(radY);
    const focalLength = 1000;
    
    return baseCorners.map(corner => {
      let x = corner.x - center.x;
      let y = corner.y - center.y;
      let z = 0;
      
      let y1 = y * cosX - z * sinX;
      let z1 = y * sinX + z * cosX;
      y = y1;
      z = z1;
      
      let x1 = x * cosY + z * sinY;
      let z2 = -x * sinY + z * cosY;
      x = x1;
      z = z2;
      
      const scaleFactor = focalLength / (focalLength + z);
      const projectedX = x * scaleFactor + center.x;
      const projectedY = y * scaleFactor + center.y;
      
      return { x: projectedX, y: projectedY };
    });
  };

  // Compute homography matrix from source rectangle to destination quadrilateral.
  // Used only for the OVERLAY's matrix3d transform (visual guide).
  const computeHomography = (src, dst) => {
    const A = [];
    const b = [];
    
    for (let i = 0; i < 4; i++) {
      const [x, y] = src[i];
      const [u, v] = dst[i];
      
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      b.push(u);
      
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      b.push(v);
    }
    
    const n = 8;
    const M = A.map((row, i) => [...row, b[i]]);
    
    for (let col = 0; col < n; col++) {
      let maxRow = col;
      let maxVal = Math.abs(M[col][col]);
      
      for (let row = col + 1; row < n; row++) {
        const val = Math.abs(M[row][col]);
        if (val > maxVal) {
          maxVal = val;
          maxRow = row;
        }
      }
      
      [M[col], M[maxRow]] = [M[maxRow], M[col]];
      
      const pivot = M[col][col];
      if (Math.abs(pivot) < 1e-10) continue;
      
      for (let j = col; j <= n; j++) {
        M[col][j] /= pivot;
      }
      
      for (let row = 0; row < n; row++) {
        if (row !== col) {
          const factor = M[row][col];
          for (let j = col; j <= n; j++) {
            M[row][j] -= factor * M[col][j];
          }
        }
      }
    }
    
    const h = M.map(row => row[n]);
    
    return [
      [h[0], h[1], h[2]],
      [h[3], h[4], h[5]],
      [h[6], h[7], 1],
    ];
  };

  // NOTE: generateProcessedImage() and recompositeFromOriginal() have been
  // REMOVED. In Schema v4 the Editor owns compositing entirely. This tool
  // never produces a composite snapshot.

  // File upload handler with Firebase Storage persistence + progress tracking
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 10 - uploadedImages.length;
    const validFiles = files.slice(0, remainingSlots);

    validFiles.forEach(async (file) => {
      // Convert HEIC/HEIF to JPEG before creating the placeholder preview
      // or reading the file. Without this, the <img> and the FileReader
      // both receive an undecodable format and the thumbnail goes blank.
      let displayFile = file;
      try {
        displayFile = await convertHeicToJpeg(file);
      } catch (convErr) {
        console.error('[HEIC] frame conversion failed:', convErr);
        // Fall through with the original file — the upload will still
        // complete, but the thumbnail will be blank rather than crashing.
      }

      const placeholderId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const localPreviewUrl = URL.createObjectURL(displayFile);
      // Preload the blob URL so the browser starts decoding immediately —
      // by the time the user drags the frame, the bitmap is cached.
      warmImageCache([localPreviewUrl]);

      // Insert an immediate placeholder so the user sees feedback
      setUploadedImages(prev => [...prev, {
        id: placeholderId,
        data: localPreviewUrl,
        originalImageData: localPreviewUrl,
        dataUrl: null,
        name: file.name,
        naturalWidth: 0,
        naturalHeight: 0,
        corners: null,
        isPlaced: false,
        scale: 1,
        zIndex: prev.length,
        rotation: { x: 0, y: 0 },
        borderWidth: 0,
        borderType: 'solid',
        borderColor: '#000000',
        gradientStartColor: '#000000',
        gradientEndColor: '#ffffff',
        originalSourceDataUrl: null,
        originalSourceNaturalWidth: 0,
        originalSourceNaturalHeight: 0,
        originalCorners: null,
        cropApplied: false,
        isUploading: true,
        uploadProgress: 25,
      }]);

      const updateProgress = (progress) => {
        setUploadedImages(prev => prev.map(img =>
          img.id === placeholderId ? { ...img, uploadProgress: progress } : img
        ));
      };

      const finalize = (payload) => {
        // Warm the cache with the final data URL BEFORE we revoke the blob
        // URL, so the browser has a decoded copy ready for drag.
        const finalSrc = payload.originalImageData || payload.dataUrl || payload.data;
        if (finalSrc) warmImageCache([finalSrc]);
        setUploadedImages(prev => prev.map(img =>
          img.id === placeholderId ? { ...img, ...payload, uploadProgress: 100, isUploading: false } : img
        ));
        try { URL.revokeObjectURL(localPreviewUrl); } catch (e) {}
      };

      try {
        const reader = new FileReader();
        reader.onerror = () => {
          setUploadedImages(prev => prev.filter(img => img.id !== placeholderId));
          try { URL.revokeObjectURL(localPreviewUrl); } catch (e) {}
        };
        reader.onload = (event) => {
          const imageDataUrl = event.target.result;
          updateProgress(55);

          const img = new Image();
          img.onerror = () => {
            setUploadedImages(prev => prev.filter(img2 => img2.id !== placeholderId));
            try { URL.revokeObjectURL(localPreviewUrl); } catch (e) {}
          };
          img.onload = async () => {
            updateProgress(75);

            // ==================================================================
            // UPLOAD-TIME DOWNSCALE — memory + storage reduction
            // ------------------------------------------------------------------
            // The raw file a user drops in is often a 12 MP phone photo.
            // We render every placed frame's artwork twice per edit session
            // (once in the overlay, once inside the composite pipeline), so
            // a 12 MP source costs ~96 MB of decoded bitmap memory per
            // frame across the app. Downscaling once here, before the image
            // ever reaches React state, caps that cost permanently and cuts
            // the Storage object size by 10-50x with no perceptible quality
            // loss for a picture frame that will be shown at a fraction of
            // the canvas resolution anyway.
            //
            // 2048px on the long edge comfortably covers any placement size
            // the frame tool supports (the canvas itself is typically
            // 1080-2160px). JPEG at 0.92 is visually lossless for this use;
            // we use PNG only when the source had alpha, which we detect by
            // sampling the canvas after drawing.
            // ==================================================================
            const MAX_EDGE = 2048;
            let targetW = img.naturalWidth;
            let targetH = img.naturalHeight;
            const longEdge = Math.max(targetW, targetH);
            if (longEdge > MAX_EDGE) {
              const ratio = MAX_EDGE / longEdge;
              targetW = Math.round(targetW * ratio);
              targetH = Math.round(targetH * ratio);
            }

            let imageDataUrlFinal = imageDataUrl;
            try {
              const downCanvas = document.createElement('canvas');
              downCanvas.width = targetW;
              downCanvas.height = targetH;
              const downCtx = downCanvas.getContext('2d', { colorSpace: 'display-p3' });
              downCtx.drawImage(img, 0, 0, targetW, targetH);

              // Detect alpha by sampling the corner pixels. If any has
              // alpha < 255, keep PNG; otherwise use JPEG for size.
              let hasAlpha = false;
              try {
                const sample = downCtx.getImageData(0, 0, 1, 1).data;
                hasAlpha = sample[3] < 255;
              } catch (e) {
                // getImageData can throw on tainted canvases — fall back to JPEG.
                hasAlpha = false;
              }

              imageDataUrlFinal = hasAlpha
                ? downCanvas.toDataURL('image/png')
                : downCanvas.toDataURL('image/jpeg', 0.92);
            } catch (downscaleErr) {
              // If anything goes wrong with the downscale, fall back to the
              // original data URL — the user still gets their frame, just at
              // full resolution.
              console.warn('[PictureFrameTool] downscale failed, using original:', downscaleErr);
              imageDataUrlFinal = imageDataUrl;
              targetW = img.naturalWidth;
              targetH = img.naturalHeight;
            }

            let storedUrl = imageDataUrlFinal;
            if (authUser) {
              try {
                const storage = getStorage();
                const frameStorageRef = storageRef(storage, `users/${authUser.uid}/frames/${placeholderId}_${file.name}`);
                await uploadString(frameStorageRef, imageDataUrlFinal, 'data_url');
                storedUrl = await getDownloadURL(frameStorageRef);
              } catch (uploadError) {
                storedUrl = imageDataUrlFinal;
              }
            }

            finalize({
              data: storedUrl,
              originalImageData: imageDataUrlFinal,
              dataUrl: imageDataUrlFinal,
              naturalWidth: targetW,
              naturalHeight: targetH,
              originalSourceDataUrl: imageDataUrlFinal,
              originalSourceNaturalWidth: targetW,
              originalSourceNaturalHeight: targetH,
              originalCorners: null,
            });
          };
          img.src = imageDataUrl;
        };
        reader.readAsDataURL(file);
      } catch (error) {
        setUploadedImages(prev => prev.filter(img => img.id !== placeholderId));
        try { URL.revokeObjectURL(localPreviewUrl); } catch (e) {}
      }
    });

    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const removedImage = uploadedImages[index];
    if (removedImage && removedImage.isPlaced && onFrameRemoved) {
      onFrameRemoved(removedImage.id);
    }
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    if (selectedImageIndex === index) {
      setSelectedImageIndex(null);
      setShowCanvasOverlay(false);
      setIsEditing(false);
      setShowFrameStyleOverlay(false);
      setShowCropOverlay(false);
      setCropRect(null);
      setCropInitialBounds(null);
    } else if (selectedImageIndex > index) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleImageSelect = (index) => {
    setSelectedImageIndex(index);
    setIsEditing(false);
    setShowCanvasOverlay(false);
    setShowFrameStyleOverlay(false);
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
  };

  const handleDeselect = () => {
    setSelectedImageIndex(null);
    setIsEditing(false);
    setShowCanvasOverlay(false);
    setShowFrameStyleOverlay(false);
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
  };

  const handlePlaceOnCanvas = () => {
    if (selectedImageIndex === null || !canvasImage) return;
    if (!bgNaturalWidth || !bgNaturalHeight) return;
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const img = uploadedImages[selectedImageIndex];
    if (!img) return;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    const { offsetX, offsetY, scale } = getImageRenderParams(rect);
    const displayedWidth = bgNaturalWidth * scale;
    const displayedHeight = bgNaturalHeight * scale;

    let width = displayedWidth * 0.4;
    let height = width / aspectRatio;
    if (height > displayedHeight * 0.6) {
      height = displayedHeight * 0.6;
      width = height * aspectRatio;
    }

    const left = offsetX + (displayedWidth - width) / 2;
    const top = offsetY + (displayedHeight - height) / 2;

    const topLeft = containerToImage(left, top, rect);
    const topRight = containerToImage(left + width, top, rect);
    const bottomRight = containerToImage(left + width, top + height, rect);
    const bottomLeft = containerToImage(left, top + height, rect);

    const corners = [topLeft, topRight, bottomRight, bottomLeft];
    const targetId = img.id;

    setIsEditing(true);
    setShowCanvasOverlay(true);
    setInteractionMode('move');
    setRotation({ x: 0, y: 0 });

    // Functional update keyed strictly by id — never by index — so only the
    // target frame is mutated regardless of render timing.
    // NO backgroundImage is stored: the Editor owns the composite.
    setUploadedImages(prev => prev.map(image => {
      if (image.id !== targetId) return image;
      return {
        ...image,
        isPlaced: true,
        corners,
        scale: 1,
        rotation: { x: 0, y: 0 },
        originalCorners: corners.map(c => ({ ...c })),
        cropApplied: false,
      };
    }));

    if (isMobile) {
      setMobilePanelVisible(false);
    }
  };

  const handleEditPlaced = (index) => {
    setSelectedImageIndex(index);
    setIsEditing(true);
    setShowCanvasOverlay(true);
    setInteractionMode('move');
    setRotation({ x: 0, y: 0 });
    setShowFrameStyleOverlay(false);
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
    if (isMobile) {
      setMobilePanelVisible(false);
    }
  };

  // ============================================
  // SAVE FRAME EDIT — NON-DESTRUCTIVE
  // ----------------------------------------------------------------------------
  // Updates ONLY this frame's metadata (corners, rotation, borders, crop
  // info). No composite snapshot is ever produced here. The Editor observes
  // the frame metadata change via onFrameApplied and rebuilds the visible
  // canvas from (working base + all frames) at render time.
  //
  // This is the fix for "edit reverts to original placed position" — the
  // rotated corners committed here are the sole source of truth for where
  // this frame renders, and nothing downstream can override them with a
  // stale snapshot.
  // ============================================
  const handleSaveChanges = async () => {
    if (selectedImageIndex === null) return;
    if (!bgNaturalWidth || !bgNaturalHeight) return;
    const selectedImg = uploadedImages[selectedImageIndex];
    if (!selectedImg) return;

    const rotatedCorners = applyRotationToCorners(selectedImg.corners, rotation.x, rotation.y);
    const targetId = selectedImg.id;

    const nextFrames = uploadedImages.map(img => {
      if (img.id !== targetId) return img;
      return {
        ...img,
        corners: rotatedCorners,
        isPlaced: true,
        rotation: { x: rotation.x, y: rotation.y },
        originalCorners: img.originalCorners || selectedImg.corners,
      };
    });

    setUploadedImages(nextFrames);

    onFrameApplied({
      id: selectedImg.id,
      originalImage: selectedImg.dataUrl || selectedImg.originalImageData,
      corners: rotatedCorners,
      rotation: { x: rotation.x, y: rotation.y },
      naturalWidth: selectedImg.naturalWidth,
      naturalHeight: selectedImg.naturalHeight,
      name: selectedImg.name,
      zIndex: selectedImg.zIndex || 0,
      borderWidth: selectedImg.borderWidth || 0,
      borderType: selectedImg.borderType || 'solid',
      borderColor: selectedImg.borderColor || '#000000',
      gradientStartColor: selectedImg.gradientStartColor || '#000000',
      gradientEndColor: selectedImg.gradientEndColor || '#ffffff',
      originalUncroppedData: selectedImg.originalSourceDataUrl,
      originalSourceNaturalWidth: selectedImg.originalSourceNaturalWidth,
      originalSourceNaturalHeight: selectedImg.originalSourceNaturalHeight,
      originalCorners: selectedImg.originalCorners || selectedImg.corners,
    });

    setIsEditing(false);
    setShowCanvasOverlay(false);
    setRotation({ x: 0, y: 0 });
    setShowFrameStyleOverlay(false);
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
    if (isMobile) setMobilePanelVisible(true);
  };

  // Imperative handle: exposes ONLY saveActiveEdit to the parent.
  // recompositeAllFrames has been REMOVED — the Editor owns compositing.
  useImperativeHandle(ref, () => ({
    saveActiveEdit: async () => {
      if (isEditing && showCanvasOverlay && selectedImageIndex !== null && uploadedImages[selectedImageIndex]?.isPlaced) {
        await handleSaveChanges();
      }
    },
  }), [isEditing, showCanvasOverlay, selectedImageIndex, uploadedImages, handleSaveChanges]);

  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowCanvasOverlay(false);
    setRotation({ x: 0, y: 0 });
    setShowFrameStyleOverlay(false);
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
    if (isMobile) {
      setMobilePanelVisible(true);
    }
  };

  const handleImageDragStart = (e) => {
    if (selectedImageIndex === null || interactionMode !== 'move') return;
    if (showFrameStyleOverlay || showCropOverlay) return;
    const point = e.touches ? e.touches[0] : e;
    const img = uploadedImages[selectedImageIndex];
    setDragState({
      type: 'image',
      startX: point.clientX,
      startY: point.clientY,
      initialCorners: img.corners.map(c => ({ ...c })),
    });
  };

  const handleCornerDragStart = (e, cornerIndex) => {
    if (selectedImageIndex === null || interactionMode !== 'move') return;
    if (showFrameStyleOverlay || showCropOverlay) return;
    e.stopPropagation();
    const point = e.touches ? e.touches[0] : e;
    const img = uploadedImages[selectedImageIndex];
    setDragState({
      type: 'corner',
      cornerIndex,
      startX: point.clientX,
      startY: point.clientY,
      initialCorners: img.corners.map(c => ({ ...c })),
    });
  };

  const handleScaleChange = (newScale) => {
    if (selectedImageIndex === null) return;
    const img = uploadedImages[selectedImageIndex];
    if (!img.corners) return;
    const oldScale = img.scale || 1;
    const scaleFactor = newScale / oldScale;
    if (scaleFactor === 1) return;

    const center = img.corners.reduce((acc, c) => ({ x: acc.x + c.x / 4, y: acc.y + c.y / 4 }), { x: 0, y: 0 });
    const newCorners = img.corners.map(c => ({
      x: center.x + (c.x - center.x) * scaleFactor,
      y: center.y + (c.y - center.y) * scaleFactor,
    }));

    setUploadedImages(prev => prev.map((image, idx) => {
      if (idx === selectedImageIndex) {
        return { ...image, corners: newCorners, scale: newScale };
      }
      return image;
    }));
  };

  // ============================================
  // CROP OVERLAY (decoupled from artwork transform)
  // ----------------------------------------------------------------------------
  // The crop rect is an INDEPENDENT, CENTERED, FIXED-SIZE overlay in the
  // container's coordinate space. It does NOT inherit the artwork's position
  // or scale. This guarantees the eight drag handles remain comfortably
  // usable even when the artwork is small or offset on the canvas.
  //
  // Cropping operations still apply to the IMAGE DATA (via homography mapping
  // back through the artwork's container-space corners), not to the layer's
  // transform.
  // ============================================
  const initializeCropRect = () => {
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container || selectedImageIndex === null) return;
    const rect = container.getBoundingClientRect();
    const img = uploadedImages[selectedImageIndex];
    if (!img) return;

    // Compute the exact same aspect-fit (contain) rect the crop overlay uses
    // to render the artwork preview. The crop grid must live strictly INSIDE
    // this rect — the image is static during crop mode, and the grid is a
    // pure crop window that can only shrink inward. This keeps the grid
    // flush with the artwork at start and prevents any spillage past the
    // art borders.
    const padding = Math.max(8, Math.min(rect.width, rect.height) * 0.05);
    const availableW = (rect.width - padding * 2) * 0.9;
    const availableH = (rect.height - padding * 2) * 0.9;
    const aspect = (img.naturalWidth > 0 && img.naturalHeight > 0)
      ? (img.naturalWidth / img.naturalHeight)
      : (4 / 3);

    let artworkW = availableW;
    let artworkH = artworkW / aspect;
    if (artworkH > availableH) {
      artworkH = availableH;
      artworkW = artworkH * aspect;
    }
    const artworkX = (rect.width - artworkW) / 2;
    const artworkY = (rect.height - artworkH) / 2;

    // Bounds clamp the grid to the artwork rect exactly. The clamp() helper
    // in the crop drag effect reads these bounds, so the grid can never be
    // dragged or resized past the artwork edge.
    const bounds = {
      x: artworkX,
      y: artworkY,
      width: artworkW,
      height: artworkH,
    };

    // Initial crop rect = the full artwork (the natural "no crop" state).
    setCropInitialBounds(bounds);
    setCropRect({ x: artworkX, y: artworkY, width: artworkW, height: artworkH });
    setCropAspectLocked(true);
  };

  const handleCropButtonClick = () => {
    setShowCropOverlay(prev => {
      const newVal = !prev;
      if (newVal) {
        setShowFrameStyleOverlay(false);
        setRotation({ x: 0, y: 0 });
        const selectedImg = uploadedImages[selectedImageIndex];
        if (selectedImg && selectedImg.corners) {
          originalCornersBeforeCropRef.current = selectedImg.corners.map(c => ({ ...c }));
        } else {
          originalCornersBeforeCropRef.current = null;
        }
        initializeCropRect();
      } else {
        setCropRect(null);
        setCropInitialBounds(null);
        setCropDrag(null);
        originalCornersBeforeCropRef.current = null;
      }
      return newVal;
    });
  };

  const handleCropApply = async () => {
    if (!cropRect || selectedImageIndex === null) return;
    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const img = uploadedImages[selectedImageIndex];
    if (!img || !img.corners) return;

    // The crop overlay renders the artwork aspect-fit (contain) into the
    // available canvas area and centers it. The image is static during crop
    // mode, so we use that same canvas-centered fit transform to reverse-map
    // the crop rect corners back to source image coordinates. This is a pure
    // translation + scale (no homography), since the crop preview is not
    // rotated or perspective-warped.
    const cropPadding = Math.max(8, Math.min(rect.width, rect.height) * 0.05);
    const cropAvailableW = (rect.width - cropPadding * 2) * 0.9;
    const cropAvailableH = (rect.height - cropPadding * 2) * 0.9;
    const fitScale = Math.min(cropAvailableW / img.naturalWidth, cropAvailableH / img.naturalHeight);
    const fittedW = img.naturalWidth * fitScale;
    const fittedH = img.naturalHeight * fitScale;
    const fittedX = (rect.width - fittedW) / 2;
    const fittedY = (rect.height - fittedH) / 2;

    const cropCornersContainer = [
      [cropRect.x, cropRect.y],
      [cropRect.x + cropRect.width, cropRect.y],
      [cropRect.x + cropRect.width, cropRect.y + cropRect.height],
      [cropRect.x, cropRect.y + cropRect.height],
    ];
    const cropCornersSource = cropCornersContainer.map(([cx, cy]) => ({
      x: (cx - fittedX) / fitScale,
      y: (cy - fittedY) / fitScale,
    }));

    const srcMinX = Math.min(...cropCornersSource.map(p => p.x));
    const srcMaxX = Math.max(...cropCornersSource.map(p => p.x));
    const srcMinY = Math.min(...cropCornersSource.map(p => p.y));
    const srcMaxY = Math.max(...cropCornersSource.map(p => p.y));
    const srcCropRect = {
      x: Math.max(0, Math.floor(srcMinX)),
      y: Math.max(0, Math.floor(srcMinY)),
      width: Math.min(img.naturalWidth, Math.ceil(srcMaxX)) - Math.max(0, Math.floor(srcMinX)),
      height: Math.min(img.naturalHeight, Math.ceil(srcMaxY)) - Math.max(0, Math.floor(srcMinY)),
    };

    if (srcCropRect.width <= 0 || srcCropRect.height <= 0) return;

    const sourceDataUrl = img.dataUrl || img.originalImageData || img.data;
    if (!sourceDataUrl) return;

    const cropImage = (srcUrl, rect) => {
      return new Promise((resolve, reject) => {
        const imgObj = new Image();
        imgObj.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = rect.width;
          canvas.height = rect.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(imgObj, rect.x, rect.y, rect.width, rect.height, 0, 0, rect.width, rect.height);
          resolve(canvas.toDataURL('image/png'));
        };
        imgObj.onerror = reject;
        imgObj.src = srcUrl;
      });
    };

    let croppedDataUrl;
    try {
      croppedDataUrl = await cropImage(sourceDataUrl, srcCropRect);
    } catch (error) {
      croppedDataUrl = sourceDataUrl;
    }

    const newNaturalWidth = srcCropRect.width;
    const newNaturalHeight = srcCropRect.height;

    // Cropping is NON-DESTRUCTIVE to the layer transform: the artwork's
    // corners (its footprint on the canvas) are NOT touched. Only the image
    // data and natural dimensions change, so the cropped content simply
    // fills the existing footprint. The scale slider remains the sole way
    // to resize the placed artwork.
    setUploadedImages(prev => prev.map((image, idx) => {
      if (idx === selectedImageIndex) {
        return {
          ...image,
          data: croppedDataUrl,
          originalImageData: croppedDataUrl,
          dataUrl: croppedDataUrl,
          naturalWidth: newNaturalWidth,
          naturalHeight: newNaturalHeight,
          originalSourceDataUrl: image.originalSourceDataUrl || sourceDataUrl,
          originalSourceNaturalWidth: image.originalSourceNaturalWidth || image.naturalWidth,
          originalSourceNaturalHeight: image.originalSourceNaturalHeight || image.naturalHeight,
          originalCorners: image.originalCorners || image.corners.map(c => ({ ...c })),
          cropApplied: true,
        };
      }
      return image;
    }));

    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
    setCropDrag(null);
    originalCornersBeforeCropRef.current = null;
  };

  const handleCropCancel = () => {
    if (originalCornersBeforeCropRef.current && selectedImageIndex !== null) {
      const originalCorners = originalCornersBeforeCropRef.current;
      setUploadedImages(prev => prev.map((img, idx) => idx === selectedImageIndex ? { ...img, corners: originalCorners } : img));
    }
    setShowCropOverlay(false);
    setCropRect(null);
    setCropInitialBounds(null);
    setCropDrag(null);
    originalCornersBeforeCropRef.current = null;
  };

  const handleCropReset = () => {
    if (selectedImageIndex === null) return;
    const img = uploadedImages[selectedImageIndex];
    if (!img) return;

    if (img.cropApplied && img.originalSourceDataUrl) {
      // Revert image data back to the original (uncropped) source.
      setUploadedImages(prev => prev.map((image, idx) => {
        if (idx === selectedImageIndex) {
          return {
            ...image,
            data: image.originalSourceDataUrl,
            originalImageData: image.originalSourceDataUrl,
            dataUrl: image.originalSourceDataUrl,
            naturalWidth: image.originalSourceNaturalWidth || image.naturalWidth,
            naturalHeight: image.originalSourceNaturalHeight || image.naturalHeight,
            cropApplied: false,
          };
        }
        return image;
      }));
    }

    // Always re-initialize the crop grid to its independent, centered state.
    initializeCropRect();
  };

  const handleCropMouseDown = (e, type, handle) => {
    if (!cropRect) return;
    e.stopPropagation();
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    setCropDrag({
      type,
      handle,
      startX: point.clientX,
      startY: point.clientY,
      initialRect: { ...cropRect },
    });
  };

  // Crop drag/resize effect (supports resize and move)
  useEffect(() => {
    if (!cropDrag) return;

    const handleMove = (e) => {
      if (!cropDrag) return;
      e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const deltaX = point.clientX - cropDrag.startX;
      const deltaY = point.clientY - cropDrag.startY;

      if (!cropInitialBounds) return;

      const clamp = (x, y, w, h) => {
        const minW = 20;
        const minH = 20;
        let newX = Math.max(cropInitialBounds.x, Math.min(x, cropInitialBounds.x + cropInitialBounds.width - w));
        let newY = Math.max(cropInitialBounds.y, Math.min(y, cropInitialBounds.y + cropInitialBounds.height - h));
        let newW = Math.max(minW, Math.min(w, cropInitialBounds.x + cropInitialBounds.width - newX));
        let newH = Math.max(minH, Math.min(h, cropInitialBounds.y + cropInitialBounds.height - newY));
        return { x: newX, y: newY, width: newW, height: newH };
      };

      if (cropDrag.type === 'resize') {
        setCropRect(prev => {
          if (!prev) return prev;
          const { x: initialX, y: initialY, width: initialWidth, height: initialHeight } = cropDrag.initialRect;
          const handle = cropDrag.handle;
          const aspectRatio = initialWidth / initialHeight;
          const minW = 20;
          const minH = 20;
          let newX = initialX;
          let newY = initialY;
          let newWidth = initialWidth;
          let newHeight = initialHeight;

          if (cropAspectLocked) {
            let fixedX = initialX;
            let fixedY = initialY;
            if (handle.includes('w')) {
              fixedX = initialX + initialWidth;
            } else if (handle.includes('e')) {
              fixedX = initialX;
            } else {
              fixedX = initialX + initialWidth / 2;
            }
            if (handle.includes('n')) {
              fixedY = initialY + initialHeight;
            } else if (handle.includes('s')) {
              fixedY = initialY;
            } else {
              fixedY = initialY + initialHeight / 2;
            }

            if (handle.includes('e') || handle.includes('w')) {
              let newW = handle.includes('e') ? initialWidth + deltaX : initialWidth - deltaX;
              newW = Math.max(minW, newW);
              newHeight = newW / aspectRatio;
              newWidth = newW;
            } else if (handle.includes('n') || handle.includes('s')) {
              let newH = handle.includes('s') ? initialHeight + deltaY : initialHeight - deltaY;
              newH = Math.max(minH, newH);
              newWidth = newH * aspectRatio;
              newHeight = newH;
            } else {
              newWidth = Math.max(minW, initialWidth + deltaX);
              newHeight = newWidth / aspectRatio;
            }

            if (handle.includes('w')) {
              newX = fixedX - newWidth;
            } else if (handle.includes('e')) {
              newX = fixedX;
            } else {
              newX = fixedX - newWidth / 2;
            }
            if (handle.includes('n')) {
              newY = fixedY - newHeight;
            } else if (handle.includes('s')) {
              newY = fixedY;
            } else {
              newY = fixedY - newHeight / 2;
            }

            return clamp(newX, newY, newWidth, newHeight);
          } else {
            if (handle.includes('e')) {
              newWidth = Math.max(minW, initialWidth + deltaX);
            }
            if (handle.includes('s')) {
              newHeight = Math.max(minH, initialHeight + deltaY);
            }
            if (handle.includes('w')) {
              newWidth = Math.max(minW, initialWidth - deltaX);
              newX = initialX + (initialWidth - newWidth);
            }
            if (handle.includes('n')) {
              newHeight = Math.max(minH, initialHeight - deltaY);
              newY = initialY + (initialHeight - newHeight);
            }
            return clamp(newX, newY, newWidth, newHeight);
          }
        });
      } else if (cropDrag.type === 'move') {
        setCropRect(prev => {
          if (!prev || !cropInitialBounds) return prev;
          const newX = Math.max(cropInitialBounds.x, Math.min(prev.x + deltaX, cropInitialBounds.x + cropInitialBounds.width - prev.width));
          const newY = Math.max(cropInitialBounds.y, Math.min(prev.y + deltaY, cropInitialBounds.y + cropInitialBounds.height - prev.height));
          return { ...prev, x: newX, y: newY };
        });
      }
    };

    const handleEnd = () => {
      setCropDrag(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [cropDrag, isMobile, mobileCanvasRef, desktopCanvasRef, cropAspectLocked, cropInitialBounds]);

  // ============================================
  // RENDER OVERLAY ON THE CANVAS
  // ----------------------------------------------------------------------------
  // ARCHITECTURE NOTE: In Schema v4, the parent Editor is already rendering
  // the full composite (base + every placed frame) via displayImageUrl.
  // This overlay therefore:
  //   - Renders `canvasImage` (the WORKING BASE, no frames) as an opaque
  //     backdrop during editing, temporarily hiding the Editor's composite.
  //   - Renders EVERY placed frame on top of that backdrop so that all layers
  //     stay visible during edit mode. Only the frame being edited receives
  //     the marching-ants highlight and interactive controls.
  //   - Draws guides (marching ants, corner handles), the control cluster,
  //     the rotation gizmo, the frame-style panel, and the crop overlay.
  //
  // The overlay's background is the CURRENT working base prop — never a
  // stored per-frame snapshot. This is what makes edits non-destructive and
  // prevents stale-position regressions.
  // ============================================
  const renderFrameOverlay = () => {
    if (!showCanvasOverlay || selectedImageIndex === null) return null;
    const selectedImg = uploadedImages[selectedImageIndex];
    if (!selectedImg || !selectedImg.corners) return null;

    const container = isMobile ? mobileCanvasRef?.current : desktopCanvasRef?.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();

    // Marching-ants parameters (selected frame only).
    const perimeter = 2 * (selectedImg.naturalWidth + selectedImg.naturalHeight);
    const strokeWidth = Math.max(1.5, Math.min(3, rect.width * 0.005));
    const dashLength = Math.max(strokeWidth * 2, perimeter / 40);
    const dashOffset = -dashLength * 2;

    // ---------------------------------------------------------------------
    // renderOneFrame(img, isSelected)
    // Renders a placed frame on the overlay using its container-space corners.
    // For the selected frame, the in-flight `rotation` state is applied; for
    // others, their persisted rotation is used.
    // ---------------------------------------------------------------------
    const renderOneFrame = (img, isSelected) => {
      if (!img || !img.corners) return null;

      const rot = isSelected ? rotation : (img.rotation || { x: 0, y: 0 });
      const rotatedCorners = applyRotationToCorners(img.corners, rot.x, rot.y);
      const containerCorners = rotatedCorners.map(c => imageToContainer(c.x, c.y, rect));
      const pixelCorners = containerCorners.map(c => [c.x, c.y]);

      const matrix3d = (() => {
        const src = [
          [0, 0],
          [img.naturalWidth, 0],
          [img.naturalWidth, img.naturalHeight],
          [0, img.naturalHeight],
        ];
        const H = computeHomography(src, pixelCorners);
        return `matrix3d(
          ${H[0][0]}, ${H[1][0]}, 0, ${H[2][0]},
          ${H[0][1]}, ${H[1][1]}, 0, ${H[2][1]},
          0, 0, 1, 0,
          ${H[0][2]}, ${H[1][2]}, 0, ${H[2][2]}
        )`;
      })();

      // Base z-index: just above the canvasImage backdrop (z=5), below the
      // crop overlay (z=20) and control cluster (z>=40). Preserves natural
      // layer ordering via img.zIndex.
      const zIndex = 6 + (img.zIndex || 0);

      // Border: the selected frame previews the in-flight frameStyle while
      // the frame-style panel is open; others use their persisted values.
      const borderProps = (isSelected && showFrameStyleOverlay) ? frameStyle : {
        borderWidth: img.borderWidth || 0,
        borderType: img.borderType || 'solid',
        borderColor: img.borderColor || '#000000',
        gradientStartColor: img.gradientStartColor || '#000000',
        gradientEndColor: img.gradientEndColor || '#ffffff',
      };
      const showBorder = borderProps.borderWidth > 0;
      const gradientId = `border-gradient-${img.id}`;

      return (
        <div
          key={img.id}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: img.naturalWidth,
            height: img.naturalHeight,
            transform: matrix3d,
            transformOrigin: '0 0',
            pointerEvents: (isSelected && interactionMode === 'move' && !showCropOverlay && !showFrameStyleOverlay) ? 'auto' : 'none',
            // ====================================================================
            // COMPOSITOR LAYER SCOPING — the mobile-OOM fix.
            // --------------------------------------------------------------------
            // `willChange: 'transform'` forces the browser to promote this
            // element to its own GPU-backed compositor layer, sized at the
            // element's layout dimensions. Our layout dimensions are the
            // frame's NATURAL pixel dimensions (e.g. 3000x4000 = 12M px),
            // so each promoted frame is a 12 MP GPU allocation.
            //
            // With the old unconditional `willChange: 'transform'`, an edit
            // session with N placed frames reserved N+1 such layers (frames
            // + canvas backdrop). Two frames survived on mobile; three
            // (edit the second frame with one already placed) blew past the
            // per-tab compositor memory cap and killed the tab.
            //
            // Only the SELECTED frame actually animates its transform
            // (during the drag gesture). Non-selected frames are static —
            // their transform string changes only on commit, which is a
            // one-off repaint the browser handles fine without a promoted
            // layer. Scoping willChange to the selected frame keeps exactly
            // ONE promoted layer alive during a drag, regardless of how
            // many frames are placed.
            //
            // `backfaceVisibility` is only relevant when a 3D transform is
            // actively animating; on static elements it also forces the
            // element onto its own layer on some engines, so we scope it
            // to the same condition.
            // ====================================================================
            willChange: isSelected ? 'transform' : 'auto',
            backfaceVisibility: isSelected ? 'hidden' : 'visible',
            WebkitBackfaceVisibility: isSelected ? 'hidden' : 'visible',
            zIndex,
          }}
        >
          <FrameArtwork
            src={img.originalImageData || img.data}
            alt={isSelected ? 'Frame on canvas' : ''}
            onMouseDown={isSelected ? handleImageDragStart : undefined}
            onTouchStart={isSelected ? handleImageDragStart : undefined}
            cursor={(isSelected && interactionMode === 'move' && !showCropOverlay && !showFrameStyleOverlay) ? 'move' : 'default'}
          />
          {/* Border preview SVG (rendered for every frame with a border). */}
          {showBorder && (
            <svg
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              {borderProps.borderType === 'gradient' && (
                <defs>
                  <linearGradient
                    id={gradientId}
                    x1="0%" y1="0%" x2="100%" y2="100%"
                  >
                    <stop offset="0%" stopColor={borderProps.gradientStartColor} />
                    <stop offset="100%" stopColor={borderProps.gradientEndColor} />
                  </linearGradient>
                </defs>
              )}
              <polygon
                points={`0,0 ${img.naturalWidth},0 ${img.naturalWidth},${img.naturalHeight} 0,${img.naturalHeight}`}
                fill="none"
                stroke={borderProps.borderType === 'gradient' ? `url(#${gradientId})` : borderProps.borderColor}
                strokeWidth={borderProps.borderWidth}
                strokeLinejoin="round"
              />
            </svg>
          )}
          {/* Marching ants outline — only on the frame being edited. */}
          {isSelected && !showCropOverlay && (
            <svg
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <polygon
                points={`0,0 ${img.naturalWidth},0 ${img.naturalWidth},${img.naturalHeight} 0,${img.naturalHeight}`}
                fill="none"
                stroke="#4594f9"
                strokeWidth={strokeWidth}
                strokeLinejoin="round"
                strokeDasharray={`${dashLength} ${dashLength}`}
                className="marching-ants"
                style={{
                  pointerEvents: 'none',
                  vectorEffect: 'non-scaling-stroke',
                  animation: `marchingAnts 1s linear infinite`,
                  '--dash-offset': `${dashOffset}px`,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
          )}
        </div>
      );
    };

    return createPortal(
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        overflow: showCropOverlay ? 'visible' : 'hidden',
        touchAction: 'none'
      }}>
        {/* Working base backdrop. Uses the CURRENT canvasImage prop — NOT a
            stored per-frame snapshot — so editing always reflects the latest
            working base. This opaque layer hides the Editor's composite
            (which still shows the pre-edit frame positions) so all frames
            can be redrawn from live metadata below. */}
        {isEditing && canvasImage && (
          <img
            src={canvasImage}
            alt=""
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: isMobile ? 'cover' : 'contain',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          />
        )}

        {/* ALL placed frames rendered on top of the backdrop.
            Non-selected frames are drawn as static layer visuals (no marching
            ants, no pointer events). Only the selected frame receives the
            edit highlight and interactive controls. This keeps every layer
            visible during edit mode, matching the desired behaviour.
            During crop mode the selected frame is hidden at its layer
            position and instead rendered fitted inside the crop window
            (see the crop overlay section below). */}
        {isEditing && uploadedImages.map((img, idx) => {
          if (!img.isPlaced || !img.corners) return null;
          if (idx === selectedImageIndex && showCropOverlay) return null;
          return renderOneFrame(img, idx === selectedImageIndex);
        })}

        {/* Rotation indicator */}
        {interactionMode === 'rotate' && !showCropOverlay && !showFrameStyleOverlay && (
          <div
            data-control-area="true"
            style={{
              position: 'absolute',
              bottom: '180px',
              left: '50%',
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              zIndex: 25,
              background: 'rgba(44, 44, 44, 0.9)',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              border: '1px solid rgba(69,148,249,0.3)',
            }}
          >
            <div style={{ textAlign: 'center', color: '#aaa', fontSize: '12px', marginBottom: '8px', fontFamily: 'Avenir, sans-serif', animation: 'rotateTextPulse 2s ease-in-out infinite' }}>
              Drag to Rotate
            </div>
            <svg width="80" height="80" viewBox="0 0 360 360" style={{ display: 'block', pointerEvents: 'none' }}>
              <line x1="30" y1="180" x2="330" y2="180" stroke="#3a3a3e" strokeWidth="1" strokeDasharray="4,4" opacity="0.5"/>
              <line x1="180" y1="30" x2="180" y2="330" stroke="#3a3a3e" strokeWidth="1" strokeDasharray="4,4" opacity="0.5"/>
              <ellipse cx="180" cy="180" rx="130" ry="35" fill="none" stroke="#4594f9" strokeWidth="6" opacity="0.9" strokeLinecap="round" strokeDasharray="200 60 200 60 200 60" className="rotate-gizmo-x"/>
              <ellipse cx="180" cy="180" rx="35" ry="130" fill="none" stroke="#4594f9" strokeWidth="6" opacity="0.9" strokeLinecap="round" strokeDasharray="200 60 200 60 200 60" className="rotate-gizmo-y"/>
            </svg>
          </div>
        )}

        {/* Frame Style Overlay */}
        {showFrameStyleOverlay && (
          <div
            data-control-area="true"
            style={{
              position: 'absolute',
              bottom: '220px',
              left: '50%',
              transform: 'translateX(-50%)',
              pointerEvents: 'auto',
              zIndex: 30,
              background: 'rgba(44, 44, 44, 0.95)',
              borderRadius: '16px',
              padding: '16px',
              border: '1px solid rgba(69,148,249,0.4)',
              width: '260px',
              maxWidth: '90%',
            }}
          >
            {/* Frame style UI - unchanged from original */}
            <div style={{ color: '#fff', fontFamily: 'Avenir, sans-serif', fontSize: '14px', marginBottom: '12px' }}>
              Frame Style
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ color: '#aaa', fontSize: '12px', minWidth: '50px' }}>Border</span>
              <input
                type="range"
                min="0"
                max="50"
                value={frameStyle.borderWidth}
                onChange={(e) => setFrameStyle(prev => ({ ...prev, borderWidth: Number(e.target.value) }))}
                className="frame-style-slider"
                style={{ flex: 1, accentColor: '#4594f9' }}
              />
              <span style={{ color: '#ddd', fontSize: '12px', minWidth: '30px', textAlign: 'right' }}>
                {frameStyle.borderWidth}px
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={() => setFrameStyle(prev => ({ ...prev, borderType: 'solid' }))}
                style={{
                  flex: 1, padding: '6px', borderRadius: '8px',
                  border: frameStyle.borderType === 'solid' ? '2px solid #4594f9' : '1px solid #555',
                  background: frameStyle.borderType === 'solid' ? 'rgba(69,148,249,0.2)' : 'transparent',
                  color: '#fff', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '12px',
                }}
              >
                Solid
              </button>
              <button
                onClick={() => setFrameStyle(prev => ({ ...prev, borderType: 'gradient' }))}
                style={{
                  flex: 1, padding: '6px', borderRadius: '8px',
                  border: frameStyle.borderType === 'gradient' ? '2px solid #4594f9' : '1px solid #555',
                  background: frameStyle.borderType === 'gradient' ? 'rgba(69,148,249,0.2)' : 'transparent',
                  color: '#fff', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '12px',
                }}
              >
                Gradient
              </button>
            </div>
            {frameStyle.borderType === 'solid' ? (
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#aaa', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Color</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => setFrameStyle(prev => ({ ...prev, borderColor: '#000000' }))}
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%', background: '#000',
                      border: frameStyle.borderColor === '#000000' ? '2px solid #4594f9' : '1px solid #555', cursor: 'pointer',
                    }}
                  />
                  <button
                    onClick={() => setFrameStyle(prev => ({ ...prev, borderColor: '#ffffff' }))}
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%', background: '#fff',
                      border: frameStyle.borderColor === '#ffffff' ? '2px solid #4594f9' : '1px solid #555', cursor: 'pointer',
                    }}
                  />
                  <div
                    style={{
                      position: 'relative', width: '32px', height: '32px', borderRadius: '50%',
                      background: 'conic-gradient(from 0deg, #ff0000, #ff8800, #ffff00, #00ff00, #0088ff, #8800ff, #ff0000)',
                      border: '1px solid #555', cursor: 'pointer', overflow: 'hidden',
                    }}
                    onClick={() => {
                      const input = document.getElementById('frame-style-color-input');
                      if (input) input.click();
                    }}
                  >
                    <input
                      id="frame-style-color-input"
                      type="color"
                      value={frameStyle.borderColor}
                      onChange={(e) => setFrameStyle(prev => ({ ...prev, borderColor: e.target.value }))}
                      style={{ position: 'absolute', top: -10, left: -10, width: '52px', height: '52px', opacity: 0, cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#aaa', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Gradient Start</span>
                <input
                  type="color"
                  value={frameStyle.gradientStartColor}
                  onChange={(e) => setFrameStyle(prev => ({ ...prev, gradientStartColor: e.target.value }))}
                  style={{ width: '100%', height: '30px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '4px' }}
                />
                <span style={{ color: '#aaa', fontSize: '12px', display: 'block', margin: '8px 0 4px' }}>Gradient End</span>
                <input
                  type="color"
                  value={frameStyle.gradientEndColor}
                  onChange={(e) => setFrameStyle(prev => ({ ...prev, gradientEndColor: e.target.value }))}
                  style={{ width: '100%', height: '30px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '4px' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setUploadedImages(prev => prev.map((img, idx) => idx === selectedImageIndex ? { ...img, ...frameStyle } : img));
                  setShowFrameStyleOverlay(false);
                }}
                style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', background: '#4594f9', color: 'white', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '12px', fontWeight: '600' }}
              >
                Apply
              </button>
              <button
                onClick={() => setShowFrameStyleOverlay(false)}
                style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#aaa', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '12px' }}
              >
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Crop Overlay */}
        {showCropOverlay && cropRect && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 20,
            }}
          >
            {/* Fitted artwork preview. The artwork is aspect-fit (contain)
                into the available canvas area and centered, so the user sees
                the whole image at a comfortable size. The image is STATIC:
                moving or resizing the crop grid must not move or scale it.
                Cropping adjustments apply to the image data itself —
                handleCropApply uses this same canvas-centered fit transform
                for the reverse mapping back to source coordinates. */}
            {selectedImg && selectedImg.naturalWidth > 0 && selectedImg.naturalHeight > 0 && (() => {
              const cropPadding = Math.max(8, Math.min(rect.width, rect.height) * 0.05);
              const cropAvailableW = (rect.width - cropPadding * 2) * 0.9;
              const cropAvailableH = (rect.height - cropPadding * 2) * 0.9;
              const fitScale = Math.min(cropAvailableW / selectedImg.naturalWidth, cropAvailableH / selectedImg.naturalHeight);
              const fittedW = selectedImg.naturalWidth * fitScale;
              const fittedH = selectedImg.naturalHeight * fitScale;
              const fittedX = (rect.width - fittedW) / 2;
              const fittedY = (rect.height - fittedH) / 2;
              return (
                <img
                  src={selectedImg.originalImageData || selectedImg.data}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: fittedX,
                    top: fittedY,
                    width: fittedW,
                    height: fittedH,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    display: 'block',
                  }}
                />
              );
            })()}
            {/* Dark overlay outside crop rect */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.7)',
                clipPath: `polygon(
                  0 0,
                  100% 0,
                  100% 100%,
                  0 100%,
                  0 ${cropRect.y}px,
                  ${cropRect.x}px ${cropRect.y}px,
                  ${cropRect.x}px ${cropRect.y + cropRect.height}px,
                  ${cropRect.x + cropRect.width}px ${cropRect.y + cropRect.height}px,
                  ${cropRect.x + cropRect.width}px ${cropRect.y}px,
                  0 ${cropRect.y}px
                )`,
                pointerEvents: 'none',
              }}
            />
            {/* Crop rectangle */}
            <div
              style={{
                position: 'absolute',
                left: cropRect.x,
                top: cropRect.y,
                width: cropRect.width,
                height: cropRect.height,
                border: '2px dashed #fff',
                boxSizing: 'border-box',
                pointerEvents: 'auto',
                zIndex: 21,
              }}
              onMouseDown={(e) => handleCropMouseDown(e, 'move', null)}
              onTouchStart={(e) => handleCropMouseDown(e, 'move', null)}
            >
              {/* Grid lines */}
              <div style={{ position: 'absolute', left: '33%', top: 0, bottom: 0, borderLeft: '1px dashed rgba(255,255,255,0.6)' }} />
              <div style={{ position: 'absolute', left: '66%', top: 0, bottom: 0, borderLeft: '1px dashed rgba(255,255,255,0.6)' }} />
              <div style={{ position: 'absolute', top: '33%', left: 0, right: 0, borderTop: '1px dashed rgba(255,255,255,0.6)' }} />
              <div style={{ position: 'absolute', top: '66%', left: 0, right: 0, borderTop: '1px dashed rgba(255,255,255,0.6)' }} />
            </div>
            {/* Handles: corners and sides */}
            {[
              { handle: 'nw', centerX: cropRect.x, centerY: cropRect.y, cursor: 'nwse-resize' },
              { handle: 'ne', centerX: cropRect.x + cropRect.width, centerY: cropRect.y, cursor: 'nesw-resize' },
              { handle: 'sw', centerX: cropRect.x, centerY: cropRect.y + cropRect.height, cursor: 'nesw-resize' },
              { handle: 'se', centerX: cropRect.x + cropRect.width, centerY: cropRect.y + cropRect.height, cursor: 'nwse-resize' },
              { handle: 'n', centerX: cropRect.x + cropRect.width / 2, centerY: cropRect.y, cursor: 'ns-resize' },
              { handle: 's', centerX: cropRect.x + cropRect.width / 2, centerY: cropRect.y + cropRect.height, cursor: 'ns-resize' },
              { handle: 'e', centerX: cropRect.x + cropRect.width, centerY: cropRect.y + cropRect.height / 2, cursor: 'ew-resize' },
              { handle: 'w', centerX: cropRect.x, centerY: cropRect.y + cropRect.height / 2, cursor: 'ew-resize' },
            ].map(({ handle, centerX, centerY, cursor }) => (
              <div
                key={handle}
                data-control-area="true"
                style={{
                  position: 'absolute',
                  left: centerX,
                  top: centerY,
                  width: 32,
                  height: 32,
                  transform: 'translate(-50%, -50%)',
                  cursor,
                  pointerEvents: 'auto',
                  zIndex: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                }}
                onMouseDown={(e) => handleCropMouseDown(e, 'resize', handle)}
                onTouchStart={(e) => handleCropMouseDown(e, 'resize', handle)}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    background: '#fff',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Main control cluster (hidden during crop) */}
        {!showCropOverlay && (
          <div 
            data-control-area="true"
            style={{
              position: 'absolute', bottom: '60px', left: '50%', transform: 'translateX(-50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
              pointerEvents: 'auto', zIndex: 40,
            }}
          >
            {!showFrameStyleOverlay && (
              <div style={{
                background: 'rgba(44, 44, 44, 0.9)', borderRadius: '16px', padding: '12px 16px',
                display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid rgba(69,148,249,0.3)',
                backdropFilter: 'blur(8px)', pointerEvents: 'auto', position: 'relative', zIndex: 45,
                userSelect: 'none', WebkitUserSelect: 'none',
              }}>
                <span style={{ color: '#aaa', fontSize: '12px', fontFamily: 'Avenir, sans-serif' }}>Scale</span>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={(selectedImg.scale || 1) * 100}
                  onChange={(e) => handleScaleChange(Number(e.target.value) / 100)}
                  className="scale-slider"
                  style={{ width: '100px', accentColor: '#4594f9', pointerEvents: 'auto', position: 'relative', zIndex: 40, touchAction: 'none', transform: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                />
                <span style={{ color: '#ddd', fontSize: '12px', minWidth: '40px', textAlign: 'right' }}>
                  {Math.round((selectedImg.scale || 1) * 100)}%
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleCropButtonClick}
                className="control-button"
                style={{
                  width: '48px', height: '48px', borderRadius: '50%', border: showCropOverlay ? '2px solid #4594f9' : '2px solid rgba(255,255,255,0.3)',
                  background: showCropOverlay ? 'rgba(69,148,249,0.3)' : 'rgba(44, 44, 44, 0.9)', color: showCropOverlay ? '#4594f9' : 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(8px)',
                }}
              >
                <IoCropSharp size={16} />
              </button>
              <button
                onClick={() => {
                  setShowFrameStyleOverlay(prev => {
                    const newVal = !prev;
                    if (newVal) {
                      setShowCropOverlay(false);
                      if (selectedImageIndex !== null) {
                        const img = uploadedImages[selectedImageIndex];
                        if (img) {
                          setFrameStyle({
                            borderWidth: img.borderWidth || 0,
                            borderType: img.borderType || 'solid',
                            borderColor: img.borderColor || '#000000',
                            gradientStartColor: img.gradientStartColor || '#000000',
                            gradientEndColor: img.gradientEndColor || '#ffffff',
                          });
                        }
                      }
                    }
                    return newVal;
                  });
                }}
                className="control-button"
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  border: showFrameStyleOverlay ? '2px solid #4594f9' : '2px solid rgba(255,255,255,0.3)',
                  background: showFrameStyleOverlay ? 'rgba(69,148,249,0.3)' : 'rgba(44, 44, 44, 0.9)',
                  color: showFrameStyleOverlay ? '#4594f9' : 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(8px)',
                }}
              >
                <MdBorderStyle size={16} />
              </button>
              <button
                onClick={() => {
                  setInteractionMode('move');
                  setShowFrameStyleOverlay(false);
                  setShowCropOverlay(false);
                }}
                className="control-button"
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  border: interactionMode === 'move' && !showFrameStyleOverlay ? '2px solid #4594f9' : '2px solid rgba(255,255,255,0.3)',
                  background: interactionMode === 'move' && !showFrameStyleOverlay ? 'rgba(69,148,249,0.3)' : 'rgba(44, 44, 44, 0.9)',
                  color: interactionMode === 'move' && !showFrameStyleOverlay ? '#4594f9' : 'white', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(8px)',
                }}
              >
                <IoIosMove size={16} />
              </button>
              <button
                onClick={() => {
                  setInteractionMode('rotate');
                  setShowFrameStyleOverlay(false);
                  setShowCropOverlay(false);
                }}
                className="control-button"
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  border: interactionMode === 'rotate' && !showFrameStyleOverlay ? '2px solid #4594f9' : '2px solid rgba(255,255,255,0.3)',
                  background: interactionMode === 'rotate' && !showFrameStyleOverlay ? 'rgba(69,148,249,0.3)' : 'rgba(44, 44, 44, 0.9)',
                  color: interactionMode === 'rotate' && !showFrameStyleOverlay ? '#4594f9' : 'white', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(8px)',
                }}
              >
                <LuRotate3D size={16} />
              </button>
              <button
                onClick={handleSaveChanges}
                className="control-button"
                style={{
                  width: '48px', height: '48px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)',
                  background: 'rgba(44, 44, 44, 0.9)', color: 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(8px)',
                }}
              >
                <LuReplace size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Crop overlay controls: X, Check, Lock/Unlock, Reset */}
        {showCropOverlay && (
          <div
            data-control-area="true"
            style={{
              position: 'absolute',
              top: '40px',
              right: '20px',
              display: 'flex',
              gap: '12px',
              pointerEvents: 'auto',
              zIndex: 50,
            }}
          >
            <button
              onClick={() => setCropAspectLocked(prev => !prev)}
              style={{
                width: '48px', height: '48px', borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'rgba(44, 44, 44, 0.9)',
                color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}
              title={cropAspectLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
            >
              {cropAspectLocked ? <FiLock size={20} /> : <FiUnlock size={20} />}
            </button>
            <button
              onClick={handleCropReset}
              style={{
                width: '48px', height: '48px', borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'rgba(44, 44, 44, 0.9)',
                color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}
              title="Reset crop"
            >
              <FiRefreshCw size={20} />
            </button>
            <button
              onClick={handleCropCancel}
              style={{
                width: '48px', height: '48px', borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'rgba(44, 44, 44, 0.9)',
                color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}
            >
              <FiX size={20} />
            </button>
            <button
              onClick={handleCropApply}
              style={{
                width: '48px', height: '48px', borderRadius: '50%',
                border: '2px solid #4594f9',
                background: 'rgba(69,148,249,0.3)',
                color: '#4594f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}
            >
              <FiCheck size={20} />
            </button>
          </div>
        )}
      </div>,
      container
    );
  };

  // Sync uploadedImages to parent via onFramesUpdate for persistence.
  // Skips while any upload is still in flight, and skips while a placed
  // frame has invalid corners (mid-drag), but DOES sync unplaced frames.
  //
  // CRITICAL — MID-DRAG GUARD:
  // The parent Editor rebuilds its composite (base + every frame) whenever
  // frameImages changes. That rebuild is a JS pixel loop plus a
  // canvas.toDataURL() of the full-resolution canvas — roughly 100-300ms
  // and ~5-10MB of heap per pass on mobile. During a frame drag the
  // RAF-throttled applyMove() pushes new corner values ~60x/sec, and if
  // this effect fires on each of those pushes the composite effect queues
  // dozens of rebuilds per second and the tab is OOM-killed.
  //
  // The fix: while a drag/edit gesture is in flight, do NOT sync to the
  // parent. handleSaveChanges() explicitly calls onFrameApplied() on
  // commit, so the parent receives the final corners exactly once. Uploads,
  // deletes, and idle selections still sync normally because dragState is
  // null and we're not inside an edit session for those actions.
  //
  // NON-DESTRUCTIVE MODEL: backgroundImage is intentionally not included in
  // the payload — the Editor's composite build uses the working base + frame
  // artwork only. The frame's `rotation` field IS included so the Editor can
  // re-apply the 3D transform during compositing.
  useEffect(() => {
    if (!onFramesUpdate || !isOpen || !hasInitializedFramesRef.current) return;

    // MID-DRAG GUARD — the crash fix. Do not push to the parent while a
    // drag is active or while a frame is being actively edited.
    if (dragState) return;
    if (isEditing && showCanvasOverlay) return;

    // Do not persist while any upload is still in flight
    if (uploadedImages.some(img => img.isUploading)) return;

    // Do not persist a frame that is mid-placement with broken corners
    const invalidFrame = uploadedImages.find(img =>
      img.isPlaced &&
      (!img.corners || !Array.isArray(img.corners) || img.corners.length !== 4 ||
       img.corners.some(c => !isFinite(c.x) || !isFinite(c.y)))
    );
    if (invalidFrame) return;

    const framesData = uploadedImages.map(img => ({
      id: img.id,
      imageData: img.data,
      originalImage: img.originalImageData || img.dataUrl || img.data,
      name: img.name,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      corners: img.corners,
      isPlaced: !!img.isPlaced,
      scale: img.scale || 1,
      rotation: img.rotation || { x: 0, y: 0 },
      zIndex: img.zIndex || 0,
      borderWidth: img.borderWidth || 0,
      borderType: img.borderType || 'solid',
      borderColor: img.borderColor || '#000000',
      gradientStartColor: img.gradientStartColor || '#000000',
      gradientEndColor: img.gradientEndColor || '#ffffff',
      originalUncroppedData: img.originalSourceDataUrl || img.originalImageData || img.data,
      originalSourceNaturalWidth: img.originalSourceNaturalWidth || img.naturalWidth,
      originalSourceNaturalHeight: img.originalSourceNaturalHeight || img.naturalHeight,
      originalCorners: img.originalCorners || img.corners,
      cropApplied: img.cropApplied || false,
    }));
    const currentSaved = savedFrames.map(f => ({ ...f }));
    if (JSON.stringify(framesData) !== JSON.stringify(currentSaved)) {
      onFramesUpdate(framesData);
    }
  }, [uploadedImages, onFramesUpdate, isOpen, savedFrames, dragState, isEditing, showCanvasOverlay]);

  // Main render: only show tool UI when isOpen is true
  return (
    <>
      {isOpen && !isMobile && (
        <div className="picture-frame-desktop-sidebar">
          <div className="picture-frame-desktop-content">
            <div className="picture-frame-header">
              <h2>Picture Frame</h2>
              <button className="picture-frame-close" onClick={onClose}>✕</button>
            </div>

            <div className="picture-frame-content" onClick={handleDeselect}>
              <div className="picture-frame-upload-section" onClick={(e) => e.stopPropagation()}>
                <button
                  className="picture-frame-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadedImages.length >= 10}
                >
                  <FiUpload size={18} />
                  Upload Images ({uploadedImages.length}/10)
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="picture-frame-review" onClick={(e) => e.stopPropagation()}>
                <div className="picture-frame-review-header">
                  <span>Uploaded Images ({uploadedImages.length})</span>
                  <button onClick={() => setShowReview(!showReview)}>
                    {showReview ? '▲' : '▼'}
                  </button>
                </div>
                {showReview && (
                  <div className="picture-frame-review-grid" onClick={(e) => {
                    if (e.target === e.currentTarget) handleDeselect();
                  }}>
                    {uploadedImages.length === 0 ? (
                      <div className="picture-frame-empty-state">
                        <FiImage size={32} color="#666" />
                        <span>No images uploaded yet</span>
                      </div>
                    ) : (
                      uploadedImages.map((img, index) => (
                        <div
                          key={img.id}
                          className={`picture-frame-review-item ${selectedImageIndex === index ? 'selected' : ''} ${img.isPlaced ? 'placed' : 'uploaded'} ${img.isUploading ? 'uploading' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (img.isUploading) return;
                            handleImageSelect(index);
                          }}
                        >
                          <img
                            src={img.data}
                            alt={`Frame ${index + 1}`}
                            style={{
                              opacity: img.isUploading ? Math.max(0.3, (img.uploadProgress || 0) / 100) : 1,
                              transition: 'opacity 0.25s ease',
                            }}
                          />
                          {img.isUploading && (
                            <div className="picture-frame-upload-progress-bar">
                              <div
                                className="picture-frame-upload-progress-fill"
                                style={{ width: `${img.uploadProgress || 0}%` }}
                              />
                            </div>
                          )}
                          {!img.isUploading && (img.isPlaced ? (
                            <div className="picture-frame-status-badge placed-badge">
                              <FiCheck size={12} />
                            </div>
                          ) : (
                            <div className="picture-frame-status-badge uploaded-badge">
                              <FiUpload size={12} />
                            </div>
                          ))}
                          {!img.isUploading && img.isPlaced && (
                            <button
                              className="picture-frame-edit-badge"
                              onClick={(e) => { e.stopPropagation(); handleEditPlaced(index); }}
                              title="Edit placed image"
                            >
                              <FiEdit2 size={12} />
                            </button>
                          )}
                          {!img.isUploading && (
                            <button
                              className="picture-frame-review-remove"
                              onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                            >
                              <FiX size={14} />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selectedImageIndex !== null && uploadedImages[selectedImageIndex] && (
                <div className="picture-frame-3d-controls" onClick={(e) => e.stopPropagation()}>
                  <div className="picture-frame-selected-info">
                    <span className="picture-frame-selected-name">
                      {uploadedImages[selectedImageIndex].name || `Frame ${selectedImageIndex + 1}`}
                    </span>
                    <div className="picture-frame-status-badge-container">
                      {uploadedImages[selectedImageIndex].isPlaced ? (
                        <span className="status-placed">Placed</span>
                      ) : (
                        <span className="status-uploaded">Uploaded</span>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="picture-frame-action-buttons">
                      <button className="picture-frame-cancel-btn" onClick={handleCancelEdit}>
                        <FiX size={16} />
                        Cancel
                      </button>
                      <button className="picture-frame-save-btn" onClick={handleSaveChanges}>
                        <FiCheck size={16} />
                        Save Changes
                      </button>
                    </div>
                  ) : (
                    uploadedImages[selectedImageIndex].isPlaced ? (
                      <div className="picture-frame-action-buttons">
                        <button className="picture-frame-edit-btn" onClick={() => handleEditPlaced(selectedImageIndex)}>
                          <FiEdit2 size={16} />
                          Edit
                        </button>
                        <button
                          className="picture-frame-remove-btn"
                          onClick={() => {
                            const currentImage = uploadedImages[selectedImageIndex];
                            if (currentImage && currentImage.isPlaced && onFrameRemoved) {
                              onFrameRemoved(currentImage.id);
                            }
                            setUploadedImages(prev => prev.map((img, idx) => {
                              if (idx === selectedImageIndex) {
                                return { ...img, isPlaced: false, corners: null, scale: 1, rotation: { x: 0, y: 0 } };
                              }
                              return img;
                            }));
                            setShowCanvasOverlay(false);
                            setIsEditing(false);
                          }}
                        >
                          <FiX size={16} />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button className="picture-frame-place-btn" onClick={handlePlaceOnCanvas} disabled={!canvasImage}>
                        <FiPlus size={18} />
                        Place on Canvas
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isOpen && isMobile && (
        <div className={`picture-frame-mobile-panel ${mobilePanelVisible ? 'open' : 'closed'}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          <div className="picture-frame-mobile-tabs">
            <div
              className={`mobile-tab gallery-tab ${mobilePanelVisible ? 'active' : ''}`}
              onClick={() => setMobilePanelVisible(!mobilePanelVisible)}
            >
              <RiPaintingLine size={20} color={mobilePanelVisible ? '#fff' : '#aaa'} />
              <span className="tab-label">Gallery</span>
            </div>
            
            {isEditing && selectedImageIndex !== null && uploadedImages[selectedImageIndex]?.isPlaced && (
              <div
                className={`mobile-tab edit-tab ${isEditing && showCanvasOverlay ? 'active' : ''}`}
                onClick={() => handleEditPlaced(selectedImageIndex)}
              >
                <FiEdit2 size={20} color={isEditing && showCanvasOverlay ? '#fff' : '#aaa'} />
                <span className="tab-label">Edit</span>
              </div>
            )}
          </div>

          {mobilePanelVisible && (
            <div className="picture-frame-mobile-content" onClick={handleDeselect}>
              <div className="picture-frame-mobile-grid" onClick={(e) => {
                if (e.target === e.currentTarget) handleDeselect();
              }}>
                {uploadedImages.map((img, index) => (
                  <div
                    key={img.id}
                    className={`picture-frame-mobile-item ${selectedImageIndex === index ? 'selected' : ''} ${img.isPlaced ? 'placed' : 'uploaded'} ${img.isUploading ? 'uploading' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (img.isUploading) return;
                      handleImageSelect(index);
                    }}
                  >
                    <img
                      src={img.data}
                      alt={`Frame ${index + 1}`}
                      style={{
                        opacity: img.isUploading ? Math.max(0.3, (img.uploadProgress || 0) / 100) : 1,
                        transition: 'opacity 0.25s ease',
                      }}
                    />
                    {img.isUploading && (
                      <div className="picture-frame-upload-progress-bar">
                        <div
                          className="picture-frame-upload-progress-fill"
                          style={{ width: `${img.uploadProgress || 0}%` }}
                        />
                      </div>
                    )}
                    {!img.isUploading && (img.isPlaced ? (
                      <div className="picture-frame-mobile-status-badge placed-mobile-badge">
                        <FiCheck size={10} />
                      </div>
                    ) : (
                      <div className="picture-frame-mobile-status-badge uploaded-mobile-badge">
                        <FiUpload size={10} />
                      </div>
                    ))}
                  </div>
                ))}
                <div
                  className="picture-frame-mobile-upload"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fileInputRef.current) fileInputRef.current.click();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fileInputRef.current) fileInputRef.current.click();
                  }}
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                >
                  <FiPlus size={24} />
                  <span>Add</span>
                </div>
              </div>

              <div className="picture-frame-mobile-controls" onClick={(e) => e.stopPropagation()}>
                {selectedImageIndex === null || !uploadedImages[selectedImageIndex] ? (
                  <button className="mobile-placeholder-btn" disabled>
                    No artwork selected
                  </button>
                ) : isEditing ? (
                  <>
                    <button className="mobile-cancel-btn" onClick={handleCancelEdit}>
                      <FiX size={16} />
                      Cancel
                    </button>
                    <button className="mobile-save-btn" onClick={handleSaveChanges}>
                      <FiCheck size={16} />
                      Save
                    </button>
                  </>
                ) : uploadedImages[selectedImageIndex].isPlaced ? (
                  <>
                    <button className="mobile-edit-btn" onClick={() => handleEditPlaced(selectedImageIndex)}>
                      <FiEdit2 size={16} />
                      Edit
                    </button>
                    <button
                      className="mobile-remove-btn"
                      onClick={() => {
                        const currentImage = uploadedImages[selectedImageIndex];
                        if (currentImage && currentImage.isPlaced && onFrameRemoved) {
                          onFrameRemoved(currentImage.id);
                        }
                        setUploadedImages(prev => prev.map((img, idx) => {
                          if (idx === selectedImageIndex) {
                            return { ...img, isPlaced: false, corners: null, scale: 1, rotation: { x: 0, y: 0 } };
                          }
                          return img;
                        }));
                        setShowCanvasOverlay(false);
                        setIsEditing(false);
                      }}
                    >
                      <FiX size={16} />
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <button className="mobile-place-btn" onClick={handlePlaceOnCanvas} disabled={!canvasImage}>
                      <FiPlus size={16} />
                      Place
                    </button>
                    <button className="mobile-remove-btn" onClick={() => handleRemoveImage(selectedImageIndex)}>
                      <FiX size={16} />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {isOpen && renderFrameOverlay()}

      {isOpen && <style>{`
        .picture-frame-desktop-sidebar {
          position: fixed;
          top: 0;
          right: 0;
          width: 340px;
          height: 100vh;
          background: #2c2c2c;
          z-index: 2000;
          border-left: 1px solid #444;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: -4px 0 24px rgba(0,0,0,0.2);
        }
        .theme-light .picture-frame-desktop-sidebar {
          background: #ffffff;
          border-left-color: #e0e0e0;
        }
        .picture-frame-desktop-content {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }
        .picture-frame-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #444;
          flex-shrink: 0;
        }
        .theme-light .picture-frame-header {
          border-bottom-color: #e0e0e0;
        }
        .picture-frame-header h2 {
          color: white;
          margin: 0;
          font-size: 18px;
          font-family: Avenir, sans-serif;
        }
        .theme-light .picture-frame-header h2 {
          color: #333;
        }
        .picture-frame-close {
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
        }
        .theme-light .picture-frame-close {
          color: #666;
        }
        .picture-frame-content {
          padding: 16px;
          overflow-y: auto;
          flex: 1;
        }
        .picture-frame-upload-section {
          margin-bottom: 12px;
        }
        .picture-frame-upload-btn {
          width: 100%;
          padding: 12px;
          border-radius: 12px;
          border: 2px dashed #555;
          background: transparent;
          color: #888;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          transition: all 0.2s ease;
        }
        .theme-light .picture-frame-upload-btn {
          border-color: #ddd;
          color: #888;
        }
        .picture-frame-upload-btn:hover:not(:disabled) {
          border-color: #4594f9;
          color: #4594f9;
        }
        .picture-frame-upload-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .picture-frame-review {
          background: #3a3a3a;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
        }
        .theme-light .picture-frame-review {
          background: #f5f5f5;
        }
        .picture-frame-review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          color: #aaa;
          font-size: 12px;
          font-family: Avenir, sans-serif;
          border-bottom: 1px solid #444;
        }
        .theme-light .picture-frame-review-header {
          border-bottom-color: #e0e0e0;
        }
        .picture-frame-review-header button {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 14px;
        }
        .picture-frame-review-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          padding: 12px;
          max-height: 200px;
          overflow-y: auto;
        }
        .picture-frame-empty-state {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          color: #666;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          gap: 8px;
        }
        .picture-frame-review-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          border: 2px solid transparent;
          aspect-ratio: 1;
          transition: all 0.2s ease;
          background: #2c2c2c;
        }
        .theme-light .picture-frame-review-item {
          background: #ffffff;
        }
        .picture-frame-review-item.selected {
          border: 2px dashed #89c2ffff !important;
        }
        .picture-frame-review-item.uploaded {
          border-color: #2196F3;
        }
        .picture-frame-review-item.placed {
          border-color: #4CAF50;
        }
        .picture-frame-review-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .picture-frame-status-badge {
          position: absolute;
          top: 4px;
          left: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          color: white;
        }
        .uploaded-badge {
          background: #2196F3;
        }
        .placed-badge {
          background: #4CAF50;
        }
        .picture-frame-edit-badge {
          position: absolute;
          bottom: 4px;
          right: 4px;
          background: #4594f9;
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }
        .picture-frame-edit-badge:hover {
          background: #3a7ed4;
        }
        .picture-frame-review-remove {
          position: absolute;
          top: 4px;
          right: 4px;
          background: rgba(0,0,0,0.6);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }
        .picture-frame-review-remove:hover {
          background: rgba(200, 0, 0, 0.8);
        }
        .picture-frame-3d-controls {
          margin-top: 4px;
        }
        .picture-frame-selected-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 4px;
          margin-bottom: 8px;
        }
        .picture-frame-selected-name {
          color: #ddd;
          font-family: Avenir, sans-serif;
          font-size: 13px;
          font-weight: 500;
        }
        .theme-light .picture-frame-selected-name {
          color: #333;
        }
        .picture-frame-status-badge-container {
          display: flex;
          gap: 6px;
        }
        .status-placed {
          background: #4CAF50;
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-family: Avenir, sans-serif;
        }
        .status-uploaded {
          background: #2196F3;
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-family: Avenir, sans-serif;
        }
        .picture-frame-place-btn {
          width: 100%;
          padding: 12px;
          border-radius: 12px;
          border: 2px dashed #4594f9;
          background: rgba(69, 148, 249, 0.1);
          color: #4594f9;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          transition: all 0.2s ease;
          margin-bottom: 12px;
        }
        .picture-frame-place-btn:hover:not(:disabled) {
          background: rgba(69, 148, 249, 0.2);
        }
        .picture-frame-place-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .picture-frame-action-buttons {
          display: flex;
          gap: 12px;
          margin-top: 4px;
        }
        .picture-frame-cancel-btn,
        .picture-frame-remove-btn {
          flex: 1;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid #555;
          background: transparent;
          color: #888;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .picture-frame-cancel-btn:hover,
        .picture-frame-remove-btn:hover {
          border-color: #888;
          color: #aaa;
        }
        .picture-frame-save-btn,
        .picture-frame-edit-btn {
          flex: 2;
          padding: 12px;
          border-radius: 12px;
          border: none;
          background: #4594f9;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          font-weight: 600;
          transition: background 0.2s ease;
        }
        .picture-frame-save-btn:hover,
        .picture-frame-edit-btn:hover {
          background: #3a7ed4;
        }

        .scale-slider, .frame-style-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          border-radius: 3px;
          background: #555;
          outline: none;
          border: 1px solid #666;
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          transform: none;
        }
        .scale-slider::-webkit-slider-thumb,
        .frame-style-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #aaa;
          cursor: pointer;
          border: 2px solid #888;
        }
        .scale-slider::-moz-range-thumb,
        .frame-style-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #aaa;
          cursor: pointer;
          border: 2px solid #888;
        }
        .scale-slider::-moz-range-track,
        .frame-style-slider::-moz-range-track {
          height: 6px;
          border-radius: 3px;
          background: #555;
          border: 1px solid #666;
        }
        .scale-slider::-moz-range-progress,
        .frame-style-slider::-moz-range-progress {
          height: 6px;
          border-radius: 3px;
          background: #4594f9;
        }

        .rotate-gizmo-x {
          animation: rotateGizmoX 4s linear infinite;
          transform-origin: center;
        }
        .rotate-gizmo-y {
          animation: rotateGizmoY 4s linear infinite;
          transform-origin: center;
        }
        @keyframes rotateGizmoX {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 820; }
        }
        @keyframes rotateGizmoY {
          0% { stroke-dashoffset: 410; }
          100% { stroke-dashoffset: 1230; }
        }

        @keyframes rotateTextPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        .picture-frame-mobile-panel {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 2000;
          display: flex;
          flex-direction: column;
          align-items: center;
          pointer-events: none;
        }
        .picture-frame-mobile-tabs {
          display: flex;
          justify-content: center;
          gap: 12px;
          pointer-events: auto;
          margin-bottom: -1px;
          z-index: 2001;
        }
        .mobile-tab {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          background: #2c2c2c;
          border-radius: 12px 12px 0 0;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: background 0.2s ease;
          border: 1px solid #444;
          border-bottom: none;
          min-width: 80px;
        }
        .theme-light .mobile-tab {
          background: #ffffff;
          border-color: #e0e0e0;
        }
        .mobile-tab.active {
          background: #4594f9;
          border-color: #4594f9;
        }
        .mobile-tab .tab-label {
          font-size: 10px;
          color: #aaa;
          margin-top: 2px;
          font-family: Avenir, sans-serif;
        }
        .mobile-tab.active .tab-label {
          color: white;
        }
        .picture-frame-mobile-content {
          width: 100%;
          background: #2c2c2c;
          border-top: 1px solid #444;
          padding: 12px 16px 20px;
          overflow-y: auto;
          max-height: calc(80vh - 60px);
          pointer-events: auto;
        }
        .theme-light .picture-frame-mobile-content {
          background: #ffffff;
          border-top-color: #e0e0e0;
        }
        .picture-frame-mobile-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          margin-bottom: 12px;
        }
        .picture-frame-mobile-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid transparent;
          cursor: pointer;
          background: #3a3a3a;
        }
        .theme-light .picture-frame-mobile-item {
          background: #f0f0f0;
        }
        .picture-frame-mobile-item.selected {
          border: 2px dashed #89c2ffff !important;
        }
        .picture-frame-mobile-item.uploaded {
          border-color: #2196F3;
        }
        .picture-frame-mobile-item.placed {
          border-color: #4CAF50;
        }
        .picture-frame-mobile-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .picture-frame-mobile-status-badge {
          position: absolute;
          top: 4px;
          left: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          color: white;
        }
        .uploaded-mobile-badge {
          background: #2196F3;
        }
        .placed-mobile-badge {
          background: #4CAF50;
        }
        .picture-frame-mobile-upload {
          aspect-ratio: 1;
          border: 2px dashed #555;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #888;
          cursor: pointer;
          font-family: Avenir, sans-serif;
          font-size: 12px;
          gap: 4px;
          transition: all 0.2s ease;
        }
        .theme-light .picture-frame-mobile-upload {
          border-color: #ddd;
        }
        .picture-frame-mobile-upload:hover {
          border-color: #4594f9;
          color: #4594f9;
        }
        .picture-frame-mobile-controls {
          display: flex;
          gap: 8px;
          margin-top: 4px;
        }
        .mobile-placeholder-btn {
          flex: 1;
          padding: 12px;
          border-radius: 12px;
          border: 1px dashed #555;
          background: transparent;
          color: #888;
          cursor: not-allowed;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          text-align: center;
        }
        .mobile-place-btn,
        .mobile-edit-btn,
        .mobile-save-btn {
          flex: 2;
          padding: 12px;
          border-radius: 12px;
          border: none;
          background: #4594f9;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          font-weight: 600;
          transition: background 0.2s ease;
        }
        .mobile-place-btn:hover:not(:disabled),
        .mobile-edit-btn:hover,
        .mobile-save-btn:hover {
          background: #3a7ed4;
        }
        .mobile-place-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .mobile-remove-btn,
        .mobile-cancel-btn {
          flex: 1;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid #555;
          background: transparent;
          color: #888;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .mobile-remove-btn:hover {
          border-color: #e02f1d;
          color: #e02f1d;
        }
        .mobile-cancel-btn:hover {
          border-color: #888;
          color: #aaa;
        }
        .corner-handle-line {
          background: transparent;
          transition: all 0.3s ease;
        }
        .corner-handle:hover .corner-handle-line,
        .corner-handle:active .corner-handle-line {
          background: #4594f9;
        }
        .corner-handle-pulse .corner-handle-line {
          animation: cornerPulse 1.2s ease-in-out infinite;
        }
        @keyframes cornerPulse {
          0%, 100% { background: rgba(84, 84, 84, 0.8); opacity: 1; }
          50% { background: #ffffff; opacity: 0.9; }
        }
        @keyframes selectedPulse {
          0% { border-color: #aaa; }
          50% { border-color: #555; }
          100% { border-color: #aaa; }
        }
        .marching-ants {
          animation: marchingAnts 1s linear infinite;
        }
        @keyframes marchingAnts {
          0% { stroke-dashoffset: var(--dash-offset); }
          100% { stroke-dashoffset: 0; }
        }

        /* Upload progress overlay — blue pill bar + percentage label */
        .picture-frame-upload-progress-bar {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 70%;
          height: 6px;
          background: rgba(0, 0, 0, 0.45);
          border-radius: 999px;
          overflow: hidden;
          z-index: 3;
          pointer-events: none;
        }
        .picture-frame-upload-progress-fill {
          height: 100%;
          background: #4594f9;
          border-radius: 999px;
          transition: width 0.2s ease-out;
        }
        .picture-frame-upload-progress-pct {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, calc(-50% + 14px));
          font-family: Avenir, sans-serif;
          font-size: 10px;
          font-weight: 600;
          color: #fff;
          text-shadow: 0 1px 2px rgba(0,0,0,0.6);
          z-index: 4;
          pointer-events: none;
          letter-spacing: 0.5px;
        }
      `}</style>}
    </>
  );
});

export default PictureFrameTool;